import { Layout, Menu } from "antd";
import { ChartColumn, ChartPie, LogOut } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { getTenantId } from "../../../redux/features/auth/authSlice";
import { useGetTenantByIdQuery } from "../../../redux/services/tenantApiSlice";
import { Page } from "../../../routes/config";
import { storage } from "../../../utils/storage";
import "./style.css";

const { Sider } = Layout;

type NavChild = { key: string; label: string; to: string };
type NavItem = {
  key: string;
  label: string;
  icon: any;
  to?: string;
  children?: NavChild[];
};

// Lucide icons default to stroke="currentColor", so deliberately NOT setting a
// color here is what lets the icon inherit the menu item's own text color —
// which is how it turns white on hover/active for free, with no extra state
// wiring. Spacing is handled in CSS (.sideBar .ant-menu-item-icon) so the
// collapsed rail can reset it cleanly. className must be forwarded: antd tags
// the icon element with "ant-menu-item-icon" through it, and every spacing
// rule in style.css hangs off that class.
const MenuIcon = ({ icon: Icon, size = 20, className }: any) => (
  <Icon
    className={["sidebarMenuIcon", className].filter(Boolean).join(" ")}
    size={size}
    strokeWidth={2}
  />
);

const getReportsNavItems = (tenantData: any): NavItem[] => {
  const features = tenantData?.result?.tenantFeatures || [];
  const hasFeature = (name: string) =>
    features.some((f: any) => f.featureName === name && f.isEnabled);

  const navItems: NavItem[] = [];

  if (hasFeature("Digitization")) {
    navItems.push({
      key: "enablement",
      label: "Enablement Report",
      icon: ChartColumn,
      to: Page.REPORTS_ENABLEMENT,
    });
  }

  if (hasFeature("TrackNTrace")) {
    navItems.push({
      key: "inventory-reports",
      label: "Inventory Reports",
      icon: ChartPie,
      children: [
        { key: "current-status", label: "Current Status", to: Page.CURRENT_STATUS },
        { key: "inventory", label: "Live Inventory", to: Page.REPORTS_INVENTORY },
        { key: "transfer-report", label: "Transfer Report", to: Page.REPORTS_TRANSFER },
        { key: "receive-report", label: "Receive Report", to: Page.REPORTS_RECEIVE },
      ],
    });
  }

  return navItems;
};

// Longest matching route wins, so /reports/inventory/details still lights up
// "Live Inventory" and detail pages never fall back to a shorter sibling.
const resolveActiveKey = (navItems: NavItem[], pathname: string): string => {
  const path = pathname.replace(/\/+$/, "") || "/";
  const hit = navItems
    .flatMap((item) => (item.children ? [item, ...item.children] : [item]))
    .filter((item): item is NavItem & { to: string } => Boolean(item.to))
    .sort((a, b) => b.to.length - a.to.length)
    .find((item) => path === item.to || path.startsWith(`${item.to}/`));
  return hit?.key ?? "enablement";
};

// Placeholder rows while the tenant's features load. Same geometry as the real
// rows (44px, 12px side margin, 8px radius) so nothing shifts when they land.
const SidebarSkeleton = ({ collapsed }: { collapsed: boolean }) => (
  <div className="sidebarSkeleton" aria-hidden="true">
    {[0, 1].map((row) => (
      <div key={row} className="sidebarSkeletonRow">
        <span className="sidebarSkeletonBlock sidebarSkeletonIcon" />
        {!collapsed && (
          <span className="sidebarSkeletonBlock sidebarSkeletonLabel" />
        )}
      </div>
    ))}
  </div>
);

type UISidebarProps = {
  collapsed: boolean;
  /** Below the mobile breakpoint the sidebar is an off-canvas drawer. */
  isMobile?: boolean;
  mobileOpen?: boolean;
};

const UISidebar = ({
  collapsed,
  isMobile = false,
  mobileOpen = false,
}: UISidebarProps) => {
  const { pathname } = useLocation();
  const tenantId = useSelector(getTenantId);
  const { data: tenantData, isLoading } = useGetTenantByIdQuery(tenantId);

  const navItems = useMemo(() => getReportsNavItems(tenantData), [tenantData]);
  const activeKey = resolveActiveKey(navItems, pathname);
  const parentKey = navItems.find((item) =>
    item.children?.some((child) => child.key === activeKey),
  )?.key;

  // Auto-expand the submenu the current route lives under instead of leaving
  // the user staring at a highlighted-but-collapsed parent row.
  const [openKeys, setOpenKeys] = useState<string[]>(["inventory-reports"]);
  useEffect(() => {
    if (parentKey) {
      setOpenKeys((prev) =>
        prev.includes(parentKey) ? prev : [...prev, parentKey],
      );
    }
  }, [parentKey]);

  const menuItems: any[] = navItems.length
    ? [
        {
          key: "group-reports",
          type: "group",
          label: "Reports",
          children: navItems.map((item) => ({
            key: item.key,
            icon: <MenuIcon icon={item.icon} />,
            label: item.to ? <Link to={item.to}>{item.label}</Link> : item.label,
            children: item.children?.map((child) => ({
              key: child.key,
              label: <Link to={child.to}>{child.label}</Link>,
            })),
          })),
        },
      ]
    : [];

  const onLogoutClick = () => {
    storage.clearAll();
    window.location.assign(Page.LOGIN);
  };

  // A hidden drawer must not stay reachable by keyboard/screen reader.
  const hiddenDrawer = isMobile && !mobileOpen;

  return (
    <Sider
      collapsed={collapsed}
      trigger={null}
      className={["sideBar", isMobile ? "sideBarMobile" : ""]
        .filter(Boolean)
        .join(" ")}
      aria-label="Main navigation"
      aria-hidden={hiddenDrawer || undefined}
      style={{
        height: "calc(100vh - var(--header-height))",
        position: "fixed",
        left: 0,
        top: "var(--header-height)",
        bottom: 0,
        overflow: "hidden",
        // antd's own ".ant-layout-sider" ships "transition: all 0.2s", but an
        // inline style always wins over it. Width AND max-width (see
        // style.css) toggle together — animating only "width" looked instant
        // on collapse because max-width snapped and clamped it. Same
        // duration/easing as the content pane's margin-left (ReportsLayout)
        // so both move as one.
        // visibility flips only after the slide-out finishes so closing the
        // drawer animates instead of vanishing.
        transition: `width 0.2s ease, max-width 0.2s ease, transform 0.25s ease, visibility 0s linear ${
          hiddenDrawer ? "0.25s" : "0s"
        }`,
        transform: hiddenDrawer ? "translateX(-100%)" : "translateX(0)",
        visibility: hiddenDrawer ? "hidden" : "visible",
      }}
    >
      <div className="sidebarNavScroll">
        {isLoading ? (
          <SidebarSkeleton collapsed={collapsed} />
        ) : (
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[activeKey]}
            openKeys={openKeys}
            onOpenChange={(keys) => setOpenKeys(keys as string[])}
            items={menuItems}
          />
        )}
      </div>
      <div className="signoutBtnContainer">
        <button
          type="button"
          title="Logout"
          aria-label="Logout"
          className="signoutBtn"
          onClick={onLogoutClick}
        >
          <LogOut className="sidebarMenuIcon" size={20} strokeWidth={2} />
          {!collapsed && <span className="signoutBtnLabel">Logout</span>}
        </button>
      </div>
    </Sider>
  );
};

export default UISidebar;
